import { IPartner } from "api/types/_news";
import { Option } from "api/types/_public";
import DatePickerCustom from "components/Common/DatePickerCustom";
import LabelWrapper from "components/Common/LabelWrapper";
import TableContainer from "components/Common/TableContainer";
import TooltipCustom from "components/Common/TooltipCustom";
import { useRole } from "components/Hooks/UserHooks";
import en from 'date-fns/locale/en-US';
import ko from 'date-fns/locale/ko';
import { ARR_INDEX_TEXT_DAY_OF_WEEK } from "helpers/constans";
import { filterMediaOptions, formatNumberWithCommas } from "helpers/format";
import { isHavePermissionRole, ROLES_FOR_APP } from "helpers/role";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import CountUp from "react-countup";
import { registerLocale } from "react-datepicker";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { Card, CardBody, CardHeader, Col, Container, Row, Spinner } from "reactstrap";
import { createSelector } from "reselect";
import { ROUTES } from "routes/allRoutes";
import { NumberParam, StringParam, useQueryParams, withDefault } from "use-query-params";
import BreadCrumb from "../../../components/Common/BreadCrumb";
import { getAllPartners, survivalStatistics as onGetSurvivalStatistics } from "../../../store/thunks";
import DropdownOption from "components/Common/DropdownOption";


const SORT_BY_DEFAULT = 'date';
const ORDER_BY_DEFAULT = 'DESC';

registerLocale('en', en);
registerLocale('ko', ko);

const SurvivalStatistic = () => {
    const { t, i18n } = useTranslation();
    const { userPermissions } = useRole();
    const navigate = useNavigate();

    const [query, setQuery]: any = useQueryParams({
        time_request: withDefault(NumberParam, 0),
        page: withDefault(NumberParam, 1),
        limit: withDefault(NumberParam, 100),
        sort_by: withDefault(StringParam, SORT_BY_DEFAULT),
        order_by: withDefault(StringParam, ORDER_BY_DEFAULT),
        start_date: withDefault(StringParam, `${moment().subtract(15, 'days').format("Y-MM-DD")}`),
        end_date: withDefault(StringParam, `${moment().format("Y-MM-DD")}`),
        media_id: withDefault(StringParam, ''),
    });

    const dispatch: any = useDispatch();

    const selectLayoutState = (state: any) => state.Statistic;

    const StatisticProperties = createSelector(
        selectLayoutState,
        (state) => ({
            survival: state.survival,
            isSurvivalSuccess: state.isSurvivalSuccess,
            isSurvivalLoading: state.isSurvivalLoading,
            error: state.error,
        })
    );

    const { survival, isSurvivalLoading } = useSelector(StatisticProperties);
    const [isCallAllOptionLoading, setIsCallAllOptionLoading] = useState<boolean>(true);

    const [dateSearch, setDateSearch] = useState<any[]>([query?.start_date ? moment(query?.start_date, 'Y-MM-DD').toDate() : null, query?.end_date ? moment(query?.end_date || '', 'Y-MM-DD').toDate() : null]);
    const [startDate, endDate] = dateSearch;


    const [mediaSearch, setMediaSearch] = useState<Option | null>(!!query?.media_id ? { value: query?.media_id, label: '' } : null);

    const [partners, setPartners] = useState<IPartner[]>([]);

    useEffect(() => {
        dispatch(onGetSurvivalStatistics(query));
    }, [JSON.stringify(query)]);

    const searchData = () => {
        setQuery({
            ...query,
            page: 1,
            start_date: startDate ? moment(new Date(startDate)).format("Y-MM-DD") : '',
            end_date: endDate ? moment(new Date(endDate)).format("Y-MM-DD") : '',
            media_id: mediaSearch?.value || '',
            time_request: + new Date()
        });
    };

    const resetData = () => {
        const queryNew = {
            ...query,
            start_date: `${moment().subtract(15, 'days').format("Y-MM-DD")}`,
            end_date: `${moment().format("Y-MM-DD")}`,
            media_id: '',
            sort_by: SORT_BY_DEFAULT,
            order_by: ORDER_BY_DEFAULT,
            time_request: + new Date(),
            page: 1,
        };
        setQuery(queryNew, "push");
        setDateSearch([moment(queryNew?.start_date || "", 'Y-MM-DD').toDate(), moment(queryNew?.end_date || "", 'Y-MM-DD').toDate()]);
        setMediaSearch({ label: t('All'), value: '' });
    };

    const renderUrlNewsTracking = (_params_url: { _partner_id: string, _date: string, _published_start: string, _published_end: string, _click_count_start?: string, _partner_news_expose_flg?: string }) => {
        const _url: string = `${ROUTES.ARTICLE_TRACKING}?partner_id=${_params_url?._partner_id || ''}&date_start=${_params_url?._date}&date_end=${_params_url?._date}&published_start=${_params_url?._published_start}&published_end=${_params_url?._published_end}&click_count_start=${_params_url?._click_count_start || ''}&partner_news_expose_flg=${_params_url?._partner_news_expose_flg || ''}`;
        return _url;
    }

    // Column
    const columns = useMemo(
        () => [
            {
                Header: t('Date'),
                accessor: "date",
                filterable: true,
                sortable: true,
                thWidth: 150,
                thClass: 'align-middle text-start',
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    const arrDate = String(cell?.value || '').split(' ');
                    const num = moment(cell?.value).days(); // 0 is Sunday , 1 is Monday, ... , 6 is Saturday
                    const classText = num === 0 ? 'text-danger row-bg-danger' : (num === 6 ? 'text-primary row-bg-primary' : '');
                    const textNote = t(`STANDS_${ARR_INDEX_TEXT_DAY_OF_WEEK[num]}`);
                    return (
                        !!item?.text_row ? <strong className="td-border-right-1px">{item?.text_row} </strong> :
                            <div className="text-start td-border-right-1px" style={{ minWidth: '120px' }}>
                                <span className={classText}>{moment(arrDate?.[0] || '', 'Y-MM-DD').format("MM-DD")} ({textNote})</span>
                            </div>

                    )
                },
            },
            {
                Header: t('Current day'),
                accessor: "d_0",
                thClass: 'text-end',
                filterable: false,
                sortable: false,
                // description: t('Description PV field each platform'),
                thWidth: 340,
                thComponent: () => (
                    <table>
                        <thead>
                            <tr>
                                <th className="text-center pb-1 none-sticky" colSpan={3}>
                                    {t('Current day')}
                                </th>
                            </tr>
                            <tr>
                                <tr>
                                    <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '110px' }}
                                    >
                                        {t('Articles')}
                                        <TooltipCustom
                                            title={t("The number of approved articles within the selected date or range.")}
                                            className="d-inline-block vertical-align-middle ms-1"
                                            style={{ transform: 'translateY(1px)' }}
                                        >
                                            <i className="ri-question-line align-bottom text-secondary" ></i>
                                        </TooltipCustom>
                                    </th>
                                    <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '120px' }}
                                    >
                                        {t('Click Articles')}
                                        <TooltipCustom
                                            title={t("The number of approved articles within the selected date or range that received at least one click on the current day.")}
                                            className="d-inline-block vertical-align-middle ms-1"
                                            style={{ transform: 'translateY(1px)' }}
                                        >
                                            <i className="ri-question-line align-bottom text-secondary" ></i>
                                        </TooltipCustom>
                                    </th>
                                    <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '110px' }}
                                    >
                                        {t('Click')}
                                        <TooltipCustom
                                            title={t("The total number of clicks on the approved articles within the selected date or range on the current day.")}
                                            className="d-inline-block vertical-align-middle ms-1"
                                            style={{ transform: 'translateY(1px)' }}
                                        >
                                            <i className="ri-question-line align-bottom text-secondary" ></i>
                                        </TooltipCustom>
                                    </th>
                                </tr>
                            </tr>
                        </thead>
                    </table>
                ),
                Cell: (cell: any) => {
                    const item: any = cell?.row?.original;
                    return (
                        <div className="d-flex text-center td-border-right-1px">
                            <div style={{ width: '110px' }}>
                                {!!item?.text_row ? <>{formatNumberWithCommas(cell?.value?.approved_article_count)}</> : <Link className='text-normal nav-link  flex-grow-1 hover-underline' to={renderUrlNewsTracking({ _partner_id: query?.media_id, _date: item?.date, _published_start: cell?.value?.date, _published_end: cell?.value?.date, _partner_news_expose_flg: '1' })} target="_blank">
                                    {formatNumberWithCommas(cell?.value?.approved_article_count)}
                                </Link>}
                            </div>
                            <div style={{ width: '120px', borderLeft: "1px solid #eaeaea", }}>
                                {!!item?.text_row ? <> {formatNumberWithCommas(cell?.value?.click_article)}</> : <Link className='text-normal nav-link  flex-grow-1 hover-underline' to={renderUrlNewsTracking({ _partner_id: query?.media_id, _date: item?.date, _published_start: cell?.value?.date, _published_end: cell?.value?.date, _partner_news_expose_flg: '1', _click_count_start: '1' })} target="_blank">
                                    {formatNumberWithCommas(cell?.value?.click_article)}
                                </Link>}
                            </div>
                            <div style={{ width: '110px', borderLeft: "1px solid #eaeaea", }}>
                                {!!item?.text_row ? <> {formatNumberWithCommas(cell?.value?.click_sum)}</> : <Link className='text-normal nav-link  flex-grow-1 hover-underline' to={renderUrlNewsTracking({ _partner_id: query?.media_id, _date: item?.date, _published_start: cell?.value?.date, _published_end: cell?.value?.date, _partner_news_expose_flg: '1' })} target="_blank">
                                    {formatNumberWithCommas(cell?.value?.click_sum)}
                                </Link>}
                            </div>
                        </div>
                    );
                },
            },
            {
                Header: t('1 day ago'),
                accessor: "d_1",
                thClass: 'text-end',
                filterable: false,
                sortable: false,
                // description: t('Description PV field each platform'),
                thWidth: 340,
                thComponent: () => (
                    <table>
                        <thead>
                            <tr>
                                <th className="text-center pb-1 none-sticky" colSpan={3}>
                                    {t('1 day ago')}
                                </th>
                            </tr>
                            <tr>
                                <tr>
                                    <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '110px' }}
                                    >
                                        {t('Articles')}
                                        <TooltipCustom
                                            title={t("The number of approved articles within the selected date or range.")}
                                            className="d-inline-block vertical-align-middle ms-1"
                                            style={{ transform: 'translateY(1px)' }}
                                        >
                                            <i className="ri-question-line align-bottom text-secondary" ></i>
                                        </TooltipCustom>
                                    </th>
                                    <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '120px' }}
                                    >
                                        {t('Click Articles')}
                                        <TooltipCustom
                                            title={t("The number of approved articles within the selected date or range that received at least one click on the current day.")}
                                            className="d-inline-block vertical-align-middle ms-1"
                                            style={{ transform: 'translateY(1px)' }}
                                        >
                                            <i className="ri-question-line align-bottom text-secondary" ></i>
                                        </TooltipCustom>
                                    </th>
                                    <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '110px' }}
                                    >
                                        {t('Click')}
                                        <TooltipCustom
                                            title={t("The total number of clicks on the approved articles within the selected date or range on the current day.")}
                                            className="d-inline-block vertical-align-middle ms-1"
                                            style={{ transform: 'translateY(1px)' }}
                                        >
                                            <i className="ri-question-line align-bottom text-secondary" ></i>
                                        </TooltipCustom>
                                    </th>
                                </tr>
                            </tr>
                        </thead>
                    </table>
                ),
                Cell: (cell: any) => {
                    const item: any = cell?.row?.original;
                    return (
                        <div className="d-flex text-center td-border-right-1px">
                            <div style={{ width: '110px' }}>
                                {!!item?.text_row ? <>{formatNumberWithCommas(cell?.value?.approved_article_count)}</> : <Link className='text-normal nav-link  flex-grow-1 hover-underline' to={renderUrlNewsTracking({ _partner_id: query?.media_id, _date: item?.date, _published_start: cell?.value?.date, _published_end: cell?.value?.date, _partner_news_expose_flg: '1' })} target="_blank">
                                    {formatNumberWithCommas(cell?.value?.approved_article_count)}
                                </Link>}
                            </div>
                            <div style={{ width: '120px', borderLeft: "1px solid #eaeaea", }}>
                                {!!item?.text_row ? <> {formatNumberWithCommas(cell?.value?.click_article)}</> : <Link className='text-normal nav-link  flex-grow-1 hover-underline' to={renderUrlNewsTracking({ _partner_id: query?.media_id, _date: item?.date, _published_start: cell?.value?.date, _published_end: cell?.value?.date, _partner_news_expose_flg: '1', _click_count_start: '1' })} target="_blank">
                                    {formatNumberWithCommas(cell?.value?.click_article)}
                                </Link>}
                            </div>
                            <div style={{ width: '110px', borderLeft: "1px solid #eaeaea", }}>
                                {!!item?.text_row ? <> {formatNumberWithCommas(cell?.value?.click_sum)}</> : <Link className='text-normal nav-link  flex-grow-1 hover-underline' to={renderUrlNewsTracking({ _partner_id: query?.media_id, _date: item?.date, _published_start: cell?.value?.date, _published_end: cell?.value?.date, _partner_news_expose_flg: '1' })} target="_blank">
                                    {formatNumberWithCommas(cell?.value?.click_sum)}
                                </Link>}
                            </div>
                        </div>
                    );
                },
            },
            {
                Header: t('2 day ago'),
                accessor: "d_2",
                thClass: 'text-end',
                filterable: false,
                sortable: false,
                // description: t('Description PV field each platform'),
                thWidth: 340,
                thComponent: () => (
                    <table>
                        <thead>
                            <tr>
                                <th className="text-center pb-1 none-sticky" colSpan={3}>
                                    {t('2 day ago')}
                                </th>
                            </tr>
                            <tr>
                                <tr>
                                    <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '110px' }}
                                    >
                                        {t('Articles')}
                                        <TooltipCustom
                                            title={t("The number of approved articles within the selected date or range.")}
                                            className="d-inline-block vertical-align-middle ms-1"
                                            style={{ transform: 'translateY(1px)' }}
                                        >
                                            <i className="ri-question-line align-bottom text-secondary" ></i>
                                        </TooltipCustom>
                                    </th>
                                    <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '120px' }}
                                    >
                                        {t('Click Articles')}
                                        <TooltipCustom
                                            title={t("The number of approved articles within the selected date or range that received at least one click on the current day.")}
                                            className="d-inline-block vertical-align-middle ms-1"
                                            style={{ transform: 'translateY(1px)' }}
                                        >
                                            <i className="ri-question-line align-bottom text-secondary" ></i>
                                        </TooltipCustom>
                                    </th>
                                    <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '110px' }}
                                    >
                                        {t('Click')}
                                        <TooltipCustom
                                            title={t("The total number of clicks on the approved articles within the selected date or range on the current day.")}
                                            className="d-inline-block vertical-align-middle ms-1"
                                            style={{ transform: 'translateY(1px)' }}
                                        >
                                            <i className="ri-question-line align-bottom text-secondary" ></i>
                                        </TooltipCustom>
                                    </th>
                                </tr>
                            </tr>
                        </thead>
                    </table>
                ),
                Cell: (cell: any) => {
                    const item: any = cell?.row?.original;
                    return (
                        <div className="d-flex text-center td-border-right-1px">
                            <div style={{ width: '110px' }}>
                                {!!item?.text_row ? <>{formatNumberWithCommas(cell?.value?.approved_article_count)}</> : <Link className='text-normal nav-link  flex-grow-1 hover-underline' to={renderUrlNewsTracking({ _partner_id: query?.media_id, _date: item?.date, _published_start: cell?.value?.date, _published_end: cell?.value?.date, _partner_news_expose_flg: '1' })} target="_blank">
                                    {formatNumberWithCommas(cell?.value?.approved_article_count)}
                                </Link>}
                            </div>
                            <div style={{ width: '120px', borderLeft: "1px solid #eaeaea", }}>
                                {!!item?.text_row ? <> {formatNumberWithCommas(cell?.value?.click_article)}</> : <Link className='text-normal nav-link  flex-grow-1 hover-underline' to={renderUrlNewsTracking({ _partner_id: query?.media_id, _date: item?.date, _published_start: cell?.value?.date, _published_end: cell?.value?.date, _partner_news_expose_flg: '1', _click_count_start: '1' })} target="_blank">
                                    {formatNumberWithCommas(cell?.value?.click_article)}
                                </Link>}
                            </div>
                            <div style={{ width: '110px', borderLeft: "1px solid #eaeaea", }}>
                                {!!item?.text_row ? <> {formatNumberWithCommas(cell?.value?.click_sum)}</> : <Link className='text-normal nav-link  flex-grow-1 hover-underline' to={renderUrlNewsTracking({ _partner_id: query?.media_id, _date: item?.date, _published_start: cell?.value?.date, _published_end: cell?.value?.date, _partner_news_expose_flg: '1' })} target="_blank">
                                    {formatNumberWithCommas(cell?.value?.click_sum)}
                                </Link>}
                            </div>
                        </div>
                    );
                },
            },
            {
                Header: t('3 day ago'),
                accessor: "d_3",
                thClass: 'text-end',
                filterable: false,
                sortable: false,
                // description: t('Description PV field each platform'),
                thWidth: 340,
                thComponent: () => (
                    <table>
                        <thead>
                            <tr>
                                <th className="text-center pb-1 none-sticky" colSpan={3}>
                                    {t('3 day ago')}
                                </th>
                            </tr>
                            <tr>
                                <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '110px' }}
                                >
                                    {t('Articles')}
                                    <TooltipCustom
                                        title={t("The number of approved articles within the selected date or range.")}
                                        className="d-inline-block vertical-align-middle ms-1"
                                        style={{ transform: 'translateY(1px)' }}
                                    >
                                        <i className="ri-question-line align-bottom text-secondary" ></i>
                                    </TooltipCustom>
                                </th>
                                <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '120px' }}
                                >
                                    {t('Click Articles')}
                                    <TooltipCustom
                                        title={t("The number of approved articles within the selected date or range that received at least one click on the current day.")}
                                        className="d-inline-block vertical-align-middle ms-1"
                                        style={{ transform: 'translateY(1px)' }}
                                    >
                                        <i className="ri-question-line align-bottom text-secondary" ></i>
                                    </TooltipCustom>
                                </th>
                                <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '110px' }}
                                >
                                    {t('Click')}
                                    <TooltipCustom
                                        title={t("The total number of clicks on the approved articles within the selected date or range on the current day.")}
                                        className="d-inline-block vertical-align-middle ms-1"
                                        style={{ transform: 'translateY(1px)' }}
                                    >
                                        <i className="ri-question-line align-bottom text-secondary" ></i>
                                    </TooltipCustom>
                                </th>
                            </tr>
                        </thead>
                    </table>
                ),
                Cell: (cell: any) => {
                    const item: any = cell?.row?.original;
                    return (
                        <div className="d-flex text-center td-border-right-1px">
                            <div style={{ width: '110px' }}>
                                {!!item?.text_row ? <>{formatNumberWithCommas(cell?.value?.approved_article_count)}</> : <Link className='text-normal nav-link  flex-grow-1 hover-underline' to={renderUrlNewsTracking({ _partner_id: query?.media_id, _date: item?.date, _published_start: cell?.value?.date, _published_end: cell?.value?.date, _partner_news_expose_flg: '1' })} target="_blank">
                                    {formatNumberWithCommas(cell?.value?.approved_article_count)}
                                </Link>}
                            </div>
                            <div style={{ width: '120px', borderLeft: "1px solid #eaeaea", }}>
                                {!!item?.text_row ? <> {formatNumberWithCommas(cell?.value?.click_article)}</> : <Link className='text-normal nav-link  flex-grow-1 hover-underline' to={renderUrlNewsTracking({ _partner_id: query?.media_id, _date: item?.date, _published_start: cell?.value?.date, _published_end: cell?.value?.date, _partner_news_expose_flg: '1', _click_count_start: '1' })} target="_blank">
                                    {formatNumberWithCommas(cell?.value?.click_article)}
                                </Link>}
                            </div>
                            <div style={{ width: '110px', borderLeft: "1px solid #eaeaea", }}>
                                {!!item?.text_row ? <> {formatNumberWithCommas(cell?.value?.click_sum)}</> : <Link className='text-normal nav-link  flex-grow-1 hover-underline' to={renderUrlNewsTracking({ _partner_id: query?.media_id, _date: item?.date, _published_start: cell?.value?.date, _published_end: cell?.value?.date, _partner_news_expose_flg: '1' })} target="_blank">
                                    {formatNumberWithCommas(cell?.value?.click_sum)}
                                </Link>}
                            </div>
                        </div>
                    );
                },
            },
            {
                Header: t('4 day ago'),
                accessor: "d_4",
                thClass: 'text-end',
                filterable: false,
                sortable: false,
                // description: t('Description PV field each platform'),
                thWidth: 340,
                thComponent: () => (
                    <table>
                        <thead>
                            <tr>
                                <th className="text-center pb-1 none-sticky" colSpan={3}>
                                    {t('4 day ago')}
                                </th>
                            </tr>
                            <tr>
                                <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '110px' }}
                                >
                                    {t('Articles')}
                                    <TooltipCustom
                                        title={t("The number of approved articles within the selected date or range.")}
                                        className="d-inline-block vertical-align-middle ms-1"
                                        style={{ transform: 'translateY(1px)' }}
                                    >
                                        <i className="ri-question-line align-bottom text-secondary" ></i>
                                    </TooltipCustom>
                                </th>
                                <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '120px' }}
                                >
                                    {t('Click Articles')}
                                    <TooltipCustom
                                        title={t("The number of approved articles within the selected date or range that received at least one click on the current day.")}
                                        className="d-inline-block vertical-align-middle ms-1"
                                        style={{ transform: 'translateY(1px)' }}
                                    >
                                        <i className="ri-question-line align-bottom text-secondary" ></i>
                                    </TooltipCustom>
                                </th>
                                <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '110px' }}
                                >
                                    {t('Click')}
                                    <TooltipCustom
                                        title={t("The total number of clicks on the approved articles within the selected date or range on the current day.")}
                                        className="d-inline-block vertical-align-middle ms-1"
                                        style={{ transform: 'translateY(1px)' }}
                                    >
                                        <i className="ri-question-line align-bottom text-secondary" ></i>
                                    </TooltipCustom>
                                </th>
                            </tr>
                        </thead>
                    </table>
                ),
                Cell: (cell: any) => {
                    const item: any = cell?.row?.original;
                    return (
                        <div className="d-flex text-center td-border-right-1px">
                            <div style={{ width: '110px' }}>
                                {!!item?.text_row ? <>{formatNumberWithCommas(cell?.value?.approved_article_count)}</> : <Link className='text-normal nav-link  flex-grow-1 hover-underline' to={renderUrlNewsTracking({ _partner_id: query?.media_id, _date: item?.date, _published_start: cell?.value?.date, _published_end: cell?.value?.date, _partner_news_expose_flg: '1' })} target="_blank">
                                    {formatNumberWithCommas(cell?.value?.approved_article_count)}
                                </Link>}
                            </div>
                            <div style={{ width: '120px', borderLeft: "1px solid #eaeaea", }}>
                                {!!item?.text_row ? <> {formatNumberWithCommas(cell?.value?.click_article)}</> : <Link className='text-normal nav-link  flex-grow-1 hover-underline' to={renderUrlNewsTracking({ _partner_id: query?.media_id, _date: item?.date, _published_start: cell?.value?.date, _published_end: cell?.value?.date, _partner_news_expose_flg: '1', _click_count_start: '1' })} target="_blank">
                                    {formatNumberWithCommas(cell?.value?.click_article)}
                                </Link>}
                            </div>
                            <div style={{ width: '110px', borderLeft: "1px solid #eaeaea", }}>
                                {!!item?.text_row ? <> {formatNumberWithCommas(cell?.value?.click_sum)}</> : <Link className='text-normal nav-link  flex-grow-1 hover-underline' to={renderUrlNewsTracking({ _partner_id: query?.media_id, _date: item?.date, _published_start: cell?.value?.date, _published_end: cell?.value?.date, _partner_news_expose_flg: '1' })} target="_blank">
                                    {formatNumberWithCommas(cell?.value?.click_sum)}
                                </Link>}
                            </div>
                        </div>
                    );
                },
            },
            {
                Header: t('5 ~ 7 days ago'),
                accessor: "ranges_5_7",
                thClass: 'text-end',
                filterable: false,
                sortable: false,
                // description: t('Description PV field each platform'),
                thWidth: 340,
                thComponent: () => (
                    <table>
                        <thead>
                            <tr>
                                <th className="text-center pb-1 none-sticky" colSpan={3}>
                                    {t('5 ~ 7 days ago')}
                                </th>
                            </tr>
                            <tr>
                                <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '110px' }}
                                >
                                    {t('Articles')}
                                    <TooltipCustom
                                        title={t("The number of approved articles within the selected date or range.")}
                                        className="d-inline-block vertical-align-middle ms-1"
                                        style={{ transform: 'translateY(1px)' }}
                                    >
                                        <i className="ri-question-line align-bottom text-secondary" ></i>
                                    </TooltipCustom>
                                </th>
                                <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '120px' }}
                                >
                                    {t('Click Articles')}
                                    <TooltipCustom
                                        title={t("The number of approved articles within the selected date or range that received at least one click on the current day.")}
                                        className="d-inline-block vertical-align-middle ms-1"
                                        style={{ transform: 'translateY(1px)' }}
                                    >
                                        <i className="ri-question-line align-bottom text-secondary" ></i>
                                    </TooltipCustom>
                                </th>
                                <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '110px' }}
                                >
                                    {t('Click')}
                                    <TooltipCustom
                                        title={t("The total number of clicks on the approved articles within the selected date or range on the current day.")}
                                        className="d-inline-block vertical-align-middle ms-1"
                                        style={{ transform: 'translateY(1px)' }}
                                    >
                                        <i className="ri-question-line align-bottom text-secondary" ></i>
                                    </TooltipCustom>
                                </th>
                            </tr>
                        </thead>
                    </table>
                ),
                Cell: (cell: any) => {
                    const item: any = cell?.row?.original;
                    return (
                        <div className="d-flex text-center td-border-right-1px">
                            <div style={{ width: '110px' }}>
                                {!!item?.text_row ? <>{formatNumberWithCommas(cell?.value?.approved_article_count)}</> : <Link className='text-normal nav-link  flex-grow-1 hover-underline' to={renderUrlNewsTracking({ _partner_id: query?.media_id, _date: item?.date, _published_start: cell?.value?.publish_at_start, _published_end: cell?.value?.publish_at_end, _partner_news_expose_flg: '1' })} target="_blank">
                                    {formatNumberWithCommas(cell?.value?.approved_article_count)}
                                </Link>}
                            </div>
                            <div style={{ width: '120px', borderLeft: "1px solid #eaeaea", }}>
                                {!!item?.text_row ? <> {formatNumberWithCommas(cell?.value?.click_article)}</> : <Link className='text-normal nav-link  flex-grow-1 hover-underline' to={renderUrlNewsTracking({ _partner_id: query?.media_id, _date: item?.date, _published_start: cell?.value?.publish_at_start, _published_end: cell?.value?.publish_at_end, _partner_news_expose_flg: '1', _click_count_start: '1' })} target="_blank">
                                    {formatNumberWithCommas(cell?.value?.click_article)}
                                </Link>}
                            </div>
                            <div style={{ width: '110px', borderLeft: "1px solid #eaeaea", }}>
                                {!!item?.text_row ? <> {formatNumberWithCommas(cell?.value?.click_sum)}</> : <Link className='text-normal nav-link  flex-grow-1 hover-underline' to={renderUrlNewsTracking({ _partner_id: query?.media_id, _date: item?.date, _published_start: cell?.value?.publish_at_start, _published_end: cell?.value?.publish_at_end, _partner_news_expose_flg: '1' })} target="_blank">
                                    {formatNumberWithCommas(cell?.value?.click_sum)}
                                </Link>}
                            </div>
                        </div>
                    );
                },
            },
            {
                Header: t('8 ~ 14 days ago'),
                accessor: "ranges_8_14",
                thClass: 'text-end',
                filterable: false,
                sortable: false,
                // description: t('Description PV field each platform'),
                thWidth: 340,
                thComponent: () => (
                    <table>
                        <thead>
                            <tr>
                                <th className="text-center pb-1 none-sticky" colSpan={3}>
                                    {t('8 ~ 14 days ago')}
                                </th>
                            </tr>
                            <tr>
                                <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '110px' }}
                                >
                                    {t('Articles')}
                                    <TooltipCustom
                                        title={t("The number of approved articles within the selected date or range.")}
                                        className="d-inline-block vertical-align-middle ms-1"
                                        style={{ transform: 'translateY(1px)' }}
                                    >
                                        <i className="ri-question-line align-bottom text-secondary" ></i>
                                    </TooltipCustom>
                                </th>
                                <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '120px' }}
                                >
                                    {t('Click Articles')}
                                    <TooltipCustom
                                        title={t("The number of approved articles within the selected date or range that received at least one click on the current day.")}
                                        className="d-inline-block vertical-align-middle ms-1"
                                        style={{ transform: 'translateY(1px)' }}
                                    >
                                        <i className="ri-question-line align-bottom text-secondary" ></i>
                                    </TooltipCustom>
                                </th>
                                <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '110px' }}
                                >
                                    {t('Click')}
                                    <TooltipCustom
                                        title={t("The total number of clicks on the approved articles within the selected date or range on the current day.")}
                                        className="d-inline-block vertical-align-middle ms-1"
                                        style={{ transform: 'translateY(1px)' }}
                                    >
                                        <i className="ri-question-line align-bottom text-secondary" ></i>
                                    </TooltipCustom>
                                </th>
                            </tr>
                        </thead>
                    </table>
                ),
                Cell: (cell: any) => {
                    const item: any = cell?.row?.original;
                    return (
                        <div className="d-flex text-center td-border-right-1px">
                            <div style={{ width: '110px' }}>
                                {!!item?.text_row ? <>{formatNumberWithCommas(cell?.value?.approved_article_count)}</> : <Link className='text-normal nav-link  flex-grow-1 hover-underline' to={renderUrlNewsTracking({ _partner_id: query?.media_id, _date: item?.date, _published_start: cell?.value?.publish_at_start, _published_end: cell?.value?.publish_at_end, _partner_news_expose_flg: '1' })} target="_blank">
                                    {formatNumberWithCommas(cell?.value?.approved_article_count)}
                                </Link>}
                            </div>
                            <div style={{ width: '120px', borderLeft: "1px solid #eaeaea", }}>
                                {!!item?.text_row ? <> {formatNumberWithCommas(cell?.value?.click_article)}</> : <Link className='text-normal nav-link  flex-grow-1 hover-underline' to={renderUrlNewsTracking({ _partner_id: query?.media_id, _date: item?.date, _published_start: cell?.value?.publish_at_start, _published_end: cell?.value?.publish_at_end, _partner_news_expose_flg: '1', _click_count_start: '1' })} target="_blank">
                                    {formatNumberWithCommas(cell?.value?.click_article)}
                                </Link>}
                            </div>
                            <div style={{ width: '110px', borderLeft: "1px solid #eaeaea", }}>
                                {!!item?.text_row ? <> {formatNumberWithCommas(cell?.value?.click_sum)}</> : <Link className='text-normal nav-link  flex-grow-1 hover-underline' to={renderUrlNewsTracking({ _partner_id: query?.media_id, _date: item?.date, _published_start: cell?.value?.publish_at_start, _published_end: cell?.value?.publish_at_end, _partner_news_expose_flg: '1' })} target="_blank">
                                    {formatNumberWithCommas(cell?.value?.click_sum)}
                                </Link>}
                            </div>
                        </div>
                    );
                },
            },
            {
                Header: t('15 days ago or more'),
                accessor: "ranges_15_60",
                thClass: 'text-end',
                filterable: false,
                sortable: false,
                // description: t('Description PV field each platform'),
                thWidth: 340,
                thComponent: () => (
                    <table>
                        <thead>
                            <tr>
                                <th className="text-center pb-1 none-sticky" colSpan={3}>
                                    {t('15 days ago or more')}
                                </th>
                            </tr>
                            <tr>
                                <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '110px' }}
                                >
                                    {t('Articles')}
                                    <TooltipCustom
                                        title={t("The number of approved articles within the selected date or range.")}
                                        className="d-inline-block vertical-align-middle ms-1"
                                        style={{ transform: 'translateY(1px)' }}
                                    >
                                        <i className="ri-question-line align-bottom text-secondary" ></i>
                                    </TooltipCustom>
                                </th>
                                <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '120px' }}
                                >
                                    {t('Click Articles')}
                                    <TooltipCustom
                                        title={t("The number of approved articles within the selected date or range that received at least one click on the current day.")}
                                        className="d-inline-block vertical-align-middle ms-1"
                                        style={{ transform: 'translateY(1px)' }}
                                    >
                                        <i className="ri-question-line align-bottom text-secondary" ></i>
                                    </TooltipCustom>
                                </th>
                                <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '110px' }}
                                >
                                    {t('Click')}
                                    <TooltipCustom
                                        title={t("The total number of clicks on the approved articles within the selected date or range on the current day.")}
                                        className="d-inline-block vertical-align-middle ms-1"
                                        style={{ transform: 'translateY(1px)' }}
                                    >
                                        <i className="ri-question-line align-bottom text-secondary" ></i>
                                    </TooltipCustom>
                                </th>
                            </tr>
                        </thead>
                    </table>
                ),
                Cell: (cell: any) => {
                    const item: any = cell?.row?.original;
                    return (
                        <div className="d-flex text-center">
                            <div style={{ width: '110px' }}>
                                {!!item?.text_row ? <>{formatNumberWithCommas(cell?.value?.approved_article_count)}</> : <Link className='text-normal nav-link  flex-grow-1 hover-underline' to={renderUrlNewsTracking({ _partner_id: query?.media_id, _date: item?.date, _published_start: cell?.value?.publish_at_start, _published_end: cell?.value?.publish_at_end, _partner_news_expose_flg: '1' })} target="_blank">
                                    {formatNumberWithCommas(cell?.value?.approved_article_count)}
                                </Link>}
                            </div>
                            <div style={{ width: '120px', borderLeft: "1px solid #eaeaea", }}>
                                {!!item?.text_row ? <> {formatNumberWithCommas(cell?.value?.click_article)}</> : <Link className='text-normal nav-link  flex-grow-1 hover-underline' to={renderUrlNewsTracking({ _partner_id: query?.media_id, _date: item?.date, _published_start: cell?.value?.publish_at_start, _published_end: cell?.value?.publish_at_end, _partner_news_expose_flg: '1', _click_count_start: '1' })} target="_blank">
                                    {formatNumberWithCommas(cell?.value?.click_article)}
                                </Link>}
                            </div>
                            <div style={{ width: '110px', borderLeft: "1px solid #eaeaea", }}>
                                {!!item?.text_row ? <> {formatNumberWithCommas(cell?.value?.click_sum)}</> : <Link className='text-normal nav-link  flex-grow-1 hover-underline' to={renderUrlNewsTracking({ _partner_id: query?.media_id, _date: item?.date, _published_start: cell?.value?.publish_at_start, _published_end: cell?.value?.publish_at_end, _partner_news_expose_flg: '1' })} target="_blank">
                                    {formatNumberWithCommas(cell?.value?.click_sum)}
                                </Link>}
                            </div>
                        </div>
                    );
                },
            },
        ],
        [i18n?.language, userPermissions, query?.media_id]
    );

    const handleChangePage = useCallback((page: any) => {
        setQuery({ page: page + 1 })
    }, []);

    const handleChangeSorting = useCallback((sortBy: any) => {
        setQuery((_prev: any) => {
            return ({ ..._prev, ...sortBy });
        });
    }, []);

    const handleChangeDateRangePicker = (values: any[] = []) => {
        setDateSearch((_prev: any) => values);
    }

    useEffect(() => {
        const handleCallAllOption = async () => {
            try {
                setIsCallAllOptionLoading((_prev) => true);
                const [res1]: any = await Promise.all([getAllPartners()]);
                setIsCallAllOptionLoading((_prev) => false);
                setPartners((_prev: any) => res1?.data);
                if (query?.media_id) {
                    const _partner = res1?.data?.find((_p: any) => String(_p?.id) === String(query?.media_id));
                    setMediaSearch({ value: `${_partner?.id}`, label: _partner?.name });
                }

            } catch (error: any) {
                setIsCallAllOptionLoading((_prev) => false);
                return error;
            }
        };
        handleCallAllOption();
    }, []);

    const categories = useMemo(() => {
        return survival?.list?.map((item: any) => item?.date).reverse() || [];
    }, [survival?.list, i18n?.language]);

    const series = useMemo(() => {
        const obChart = survival?.list?.reduce((ob: any, item: any) => {
            ob.impression?.push(Number(String(item?.impression_sum).replace(/,/g, '')));
            ob.click?.push(Number(String(item?.click_sum).replace(/,/g, '')));
            ob.ctr?.push(Number(String(item?.ctr_sum).replace(/,/g, '')));
            return ob;
        }, { impression: [], click: [], ctr: [] }) || [];
        return (
            [
                {
                    name: t('CTR'),
                    type: 'line',
                    data: obChart?.ctr?.reverse(),
                },
                {
                    name: t('Click'),
                    type: 'line',
                    data: obChart?.click?.reverse(),
                },
                {
                    name: t('Impression'),
                    type: 'column',
                    data: obChart?.impression?.reverse(),
                },
            ]
        )
    }, [survival?.list, i18n?.language]);


    useEffect(() => {
        document.title = `${t('Survival Statistics')} | ${t('PRESS Ai')}`;
        document.body.classList.remove('vertical-sidebar-enable');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n?.language]);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title={t('Survival Statistics')} pageTitle={t('Statistics')} />
                    {isHavePermissionRole(ROLES_FOR_APP.SURVIVAL_STATISTICS, userPermissions) && (
                        <Row>
                            <Col sm={12} md={12}>
                                <Card id="customerList" style={{ boxShadow: '0px 3px 4px 0px rgba(0, 0, 0, 0.03)' }} className="ribbon-box right">
                                    <div className="ribbon round-shape" style={{ background: 'rgb(40 99 213)', fontWeight: 300, fontSize: '0.8rem', top: '11px' }}>{t('This data is analyzed per 10 mins')}</div>
                                    <CardHeader className="border-0" style={{ borderRadius: '30px' }}>
                                        <Row className="g-4 align-items-center">
                                            <div className="col-sm">
                                                <div>
                                                    <h5 className="card-title mb-0">
                                                        <span className="me-2">{t('Total')}:</span>
                                                        {(isSurvivalLoading) ? <Spinner size="sm" className="text-primary"></Spinner> : (<CountUp
                                                            start={0}
                                                            end={survival?.pagination?.total || 0}
                                                            duration={1}
                                                            className="text-primary"
                                                        />)}
                                                    </h5>
                                                </div>
                                            </div>
                                        </Row>
                                    </CardHeader>
                                    <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-4">
                                        <Row className="g-4 align-items-center mt-0">
                                            <Col sm={6} md={4} xl={3} xxl={3} className='mt-3 mt-md-2'>
                                                <LabelWrapper label={t('Media')} isShow={true}>
                                                    <DropdownOption
                                                        name="media"
                                                        dataList={filterMediaOptions(partners || [])?.map((_p: any) => ({ label: _p?.name, value: `${_p?.id}` }))}
                                                        placeholder={`${t("Media")}...`}
                                                        className="dropdown-status-rounded z-hight"
                                                        classNamePrefix="name-prefix"
                                                        initialValue={mediaSearch || null}
                                                        onChangeSelect={(e: any) => setMediaSearch(e)}
                                                        isHasOptionAll={true}
                                                        optionAll={{ label: t('All'), value: '' }}
                                                    />
                                                </LabelWrapper>
                                            </Col>
                                            <Col sm={6} md={4} xl={3} xxl={3} className='date-picker-wrapper-custom mt-3 mt-md-2'>
                                                <LabelWrapper label={t('Date')} isShow={!!startDate || !!endDate}>
                                                    <DatePickerCustom
                                                        placeholder={`${t('Start Date')} - ${t('End Date')}`}
                                                        startDate={startDate || null}
                                                        endDate={endDate || null}
                                                        onChangePicker={handleChangeDateRangePicker}
                                                        showOptions={[
                                                            'today', 'yesterday', 'two_day_ago',
                                                            'last_3_days', 'last_7_days', 'last_15_days', 'last_30_days',
                                                            'this_week', 'last_week',
                                                            'this_month', 'last_month',
                                                        ]}
                                                    />
                                                </LabelWrapper>
                                            </Col>
                                            <Col sm={12} md={4} xl={6} xxl={6} className="hstack gap-1 justify-content-center justify-content-sm-end justify-content-md-between mt-3 mt-md-2">
                                                <div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary me-2 "
                                                        onClick={searchData}
                                                        disabled={(isSurvivalLoading || isCallAllOptionLoading)}
                                                    >
                                                        {(isSurvivalLoading || isCallAllOptionLoading) ? <div className="me-1" style={{ display: 'inline-table', verticalAlign: 'top' }}><Spinner size="sm" style={{ width: '14px', height: '14px' }}></Spinner></div> : <i className="ri-search-line align-bottom me-1"></i>}{" "}
                                                        {t('Button Search')}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary fs-14"
                                                        onClick={resetData}
                                                    >
                                                        <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                                                        {t('Button Reset')}
                                                    </button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                    {/* <ChartLine
                                        className="my-3"
                                        titles={[t('CTR'), t('Click'), t('Impression')]}
                                        categories={categories}
                                        series={series}
                                    /> */}
                                    <CardBody className="pt-3">
                                        <TableContainer
                                            className="custom-header-css"
                                            divClass="table-responsive table-card"
                                            tableClass="align-middle table-bordered-dashed table-have-first-row-is-total sticky-table-survival-statistics"
                                            theadClass="table-light text-muted"
                                            columns={columns}
                                            data={survival?.list?.length ? (
                                                [
                                                    {
                                                        ...survival?.total,
                                                        text_row: t('Sum')
                                                    },
                                                    ...survival?.list]
                                            ) : []}
                                            customPageSize={query.limit}
                                            customPageIndex={query.page - 1}
                                            totalRecords={survival?.pagination?.total}
                                            customPageCount={Math.ceil(Number(survival?.pagination?.total) / Number(survival?.pagination?.limit))}
                                            handleChangePage={handleChangePage}
                                            manualSorting={true}
                                            sorting={{ sort_by: query.sort_by, order_by: query.order_by }}
                                            handleChangeSorting={handleChangeSorting}
                                            isLoading={isSurvivalLoading}
                                            isShowPagination={false}
                                        />
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    )}
                    <ToastContainer closeButton={false} limit={1} />
                </Container>
            </div>
        </React.Fragment>
    );
};

export default SurvivalStatistic;
